import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RefreshCw, Inbox, ArrowUpRight, Download } from "lucide-react";
import {
  fetchRequests,
  fetchSummary,
  buildRequestsCsvExportUrl,
} from "@/admin/requestsAdminApi";
import {
  REQUEST_TYPES,
  STATUS_LABELS,
  TYPE_LABELS,
  formatDate,
  submitterContact,
  submitterName,
} from "@/admin/requestsLabels";

const STATUS_FILTERS = [
  { key: "",            label: "All statuses" },
  { key: "new",         label: "New" },
  { key: "in_progress", label: "In progress" },
  { key: "done",        label: "Done" },
];

function StatusBadge({ status }) {
  const palette =
    status === "new"
      ? { bg: "var(--sas-accent-soft)", fg: "var(--sas-accent)" }
      : status === "in_progress"
      ? { bg: "#fef3c7", fg: "#92400e" }
      : { bg: "#e0f2fe", fg: "#075985" };
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium"
      style={{ background: palette.bg, color: palette.fg }}
      data-testid={`request-status-${status}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function AdminRequestsList() {
  const [params, setParams] = useSearchParams();
  const activeType = params.get("type") || "";
  const activeStatus = params.get("status") || "";

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useMemo(
    () => async () => {
      setLoading(true);
      setError("");
      try {
        const [s, page] = await Promise.all([
          fetchSummary(),
          fetchRequests({ type: activeType, statusFilter: activeStatus, limit: 50 }),
        ]);
        setSummary(s);
        setItems(page.items || []);
      } catch (e) {
        setError(e.message || "Failed to load requests.");
      } finally {
        setLoading(false);
      }
    },
    [activeType, activeStatus]
  );

  useEffect(() => { load(); }, [load]);

  const byType = (summary && summary.by_type) || [];
  const totalNew = (summary && summary.total_new) || 0;
  const grandTotal = (summary && summary.total) || 0;

  const setFilter = (next) => {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (v === "" || v == null) merged.delete(k);
      else merged.set(k, v);
    });
    setParams(merged, { replace: false });
  };

  return (
    <div data-testid="admin-requests-list">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="sas-eyebrow mb-2">Requests</div>
          <h1 className="sas-h1" data-testid="admin-requests-heading">Incoming requests</h1>
          <p className="sas-body mt-3 max-w-2xl text-[15px]">
            Submissions from the public contact forms. Requests are grouped by type
            and can be moved through <em>New → In progress → Done</em> as you work them.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="admin-requests-actions">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
            data-testid="admin-requests-refresh"
          >
            <RefreshCw size={14} aria-hidden />
            Refresh
          </button>
          <a
            href={buildRequestsCsvExportUrl({
              type: activeType,
              statusFilter: activeStatus,
            })}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
            data-testid="admin-requests-export-csv"
            title="Export current filter as CSV"
            download
          >
            <Download size={14} aria-hidden />
            Export CSV
          </a>
        </div>
      </div>

      {/* Category tabs */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-requests-tabs">
        <TypeTab
          active={activeType === ""}
          onClick={() => setFilter({ type: "" })}
          label="All"
          count={grandTotal}
          newCount={totalNew}
          testId="admin-type-tab-all"
        />
        {REQUEST_TYPES.map((t) => {
          const meta = byType.find((m) => m.type === t);
          return (
            <TypeTab
              key={t}
              active={activeType === t}
              onClick={() => setFilter({ type: t })}
              label={TYPE_LABELS[t]}
              count={meta ? meta.count : 0}
              newCount={meta ? meta.new_count : 0}
              testId={`admin-type-tab-${t}`}
            />
          );
        })}
      </div>

      {/* Status secondary filter */}
      <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="admin-requests-status-filter">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.key || "all"}
            type="button"
            onClick={() => setFilter({ status: s.key })}
            className={`px-3 py-1.5 rounded-full border transition-colors ${
              activeStatus === s.key
                ? "border-[color:var(--sas-ink)] text-[color:var(--sas-ink)] bg-[color:var(--sas-accent-soft)]/50"
                : "border-[color:var(--sas-line)] text-[color:var(--sas-ink-2)] hover:border-[color:var(--sas-ink-2)]"
            }`}
            data-testid={`admin-status-filter-${s.key || "all"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="mt-6">
        {loading ? (
          <ListSkeleton />
        ) : error ? (
          <EmptyState title="Couldn’t load requests" body={error} />
        ) : items.length === 0 ? (
          <EmptyState
            title="No requests yet"
            body={
              activeType || activeStatus
                ? "No submissions match the current filter."
                : "Submissions from the contact forms will appear here as soon as they arrive."
            }
          />
        ) : (
          <ul className="divide-y divide-[color:var(--sas-line)] border border-[color:var(--sas-line)] rounded-xl overflow-hidden bg-white" data-testid="admin-requests-rows">
            {items.map((it) => (
              <li key={it.id} data-testid={`admin-request-row-${it.id}`}>
                <Link
                  to={`/admin/requests/${it.id}`}
                  className="flex items-center gap-4 px-4 py-4 hover:bg-[color:var(--sas-accent-soft)]/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={it.status} />
                      <span className="text-[11px] font-semibold tracking-wide uppercase text-[color:var(--sas-ink-2)]">
                        {it.category_label}
                      </span>
                      <span className="text-[11px] text-[color:var(--sas-ink-2)]">
                        · {formatDate(it.submitted_at)} · {it.language?.toUpperCase()}
                      </span>
                    </div>
                    <div className="mt-1 text-[14px] font-semibold text-[color:var(--sas-ink)] truncate">
                      {submitterName(it)}
                    </div>
                    <div className="mt-0.5 text-[12.5px] text-[color:var(--sas-ink-2)] truncate">
                      {submitterContact(it)}
                    </div>
                  </div>
                  <ArrowUpRight size={16} className="shrink-0 text-[color:var(--sas-ink-2)]" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TypeTab({ active, onClick, label, count, newCount, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-[13px] transition-colors ${
        active
          ? "border-[color:var(--sas-ink)] bg-[color:var(--sas-ink)] text-white"
          : "border-[color:var(--sas-line)] text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)]"
      }`}
      data-testid={testId}
      aria-pressed={active}
    >
      {label}
      <span
        className={`inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full text-[11px] px-1.5 ${
          active
            ? "bg-white/15 text-white"
            : "bg-[color:var(--sas-accent-soft)] text-[color:var(--sas-accent)]"
        }`}
        aria-label={`${count} total`}
      >
        {count}
      </span>
      {newCount > 0 && !active && (
        <span
          className="inline-flex items-center justify-center min-w-[20px] h-[20px] rounded-full text-[10px] px-1 bg-[#fee2e2] text-[#991b1b]"
          aria-label={`${newCount} new`}
          title={`${newCount} new`}
        >
          {newCount}
        </span>
      )}
    </button>
  );
}

function EmptyState({ title, body }) {
  return (
    <div
      className="border border-dashed border-[color:var(--sas-line)] rounded-xl py-16 px-6 text-center"
      data-testid="admin-requests-empty"
    >
      <div
        className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4"
        style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
        aria-hidden
      >
        <Inbox size={22} />
      </div>
      <h2 className="sas-h3">{title}</h2>
      <p className="sas-body mt-2 max-w-md mx-auto text-[14px]">{body}</p>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2" data-testid="admin-requests-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="border border-[color:var(--sas-line)] rounded-xl h-16 bg-white animate-pulse"
        />
      ))}
    </div>
  );
}
